<script lang="ts" setup>
import { ArrowLongLeftIcon, ArrowLongRightIcon } from '@heroicons/vue/20/solid';
import SearchPaginationButton from './SearchPaginationButton.vue';
import { computed, type Ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  page: { type: Number, required: true },
  pages: { type: Number, required: true },
});

const { t } = useI18n();

const { page, pages } = toRefs(props);

function usePagination(currentPageRef: Ref<number>, totalPagesRef: Ref<number>) {
  return computed(() => {
    const currentPage = currentPageRef.value;
    const totalPages = totalPagesRef.value;
    const siblingCount = 1;

    const range = (start: number, end: number) => Array.from({ length: end - start + 1 }, (_, i) => start + i);

    const totalVisible = siblingCount * 2 + 5; // Previous, Next, and sibling pages
    if (totalPages <= totalVisible) {
      return range(1, totalPages);
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 2;

    const pagesArray: (number | '...')[] = [];
    if (shouldShowLeftDots) {
      pagesArray.push(1, '...');
    } else {
      pagesArray.push(...range(1, leftSiblingIndex - 1));
    }

    pagesArray.push(...range(leftSiblingIndex, rightSiblingIndex));

    if (shouldShowRightDots) {
      pagesArray.push('...', totalPages);
    } else {
      pagesArray.push(...range(rightSiblingIndex + 1, totalPages));
    }

    return pagesArray;
  });
}

const pageNumbers = usePagination(page, pages);
</script>

<template>
  <nav class="flex items-center justify-between border-t border-gray-200 px-4 sm:px-0">
    <SearchPaginationButton :disabled="page <= 1" :page="page - 1">
      <ArrowLongLeftIcon aria-hidden="true" class="mr-3 size-5 text-gray-400" />
      {{ t('views.search.results.pagination.previous') }}
    </SearchPaginationButton>

    <div class="hidden md:-mt-px md:flex">
      <template v-for="item in pageNumbers" :key="item">
        <SearchPaginationButton v-if="item !== '...'" :active="item === page" :page="item">
          {{ item }}
        </SearchPaginationButton>
        <span v-else class="inline-flex items-center px-4 pt-4 text-sm font-medium text-gray-500"> ... </span>
      </template>
    </div>

    <SearchPaginationButton :disabled="page >= pages" :page="page + 1">
      {{ t('views.search.results.pagination.next') }}
      <ArrowLongRightIcon aria-hidden="true" class="ml-3 size-5 text-gray-400" />
    </SearchPaginationButton>
  </nav>
</template>
